import { FORM_DATE_FORMAT } from 'constants/dates';
import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { InvoiceService, CounterpartyService } from 'api';
import {
  ClausesQueryParams,
  InvoiceClassQueryParams,
} from 'api/invoices/invoices.types';
import { isSuccessful } from 'api/utils';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import {
  MessageContextActionType,
  useMessageContext,
} from 'contexts/messageContext';
import { addDays, format } from 'date-fns';
import _ from 'lodash';
import {
  Clause,
  CorrectiveInvoice,
  Counterparty,
  CurrencyCode,
  Invoice,
  InvoiceClass,
} from 'pages/invoices/invoices.types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { InvoiceTab, InvoiceType, reactPaths } from 'root/pathnames';
import { InvoicesParams } from 'types/invoices';
import { dateStringToEpoch } from 'utils/dateFunctions.utils';
import { jsonIdDecode } from 'utils/decoders';
import {
  getCorrectedExchangeRatesPayload,
  getExchangeRatesPayload,
  isInvoiceAdministrative,
  isInvoiceCorrective,
  isInvoiceInternal,
  recalculateAllCorrectedExchangeRates,
  recalculateAllExchangeRates,
  recalculateCorrectedTotals,
  recalculateTotals,
} from 'utils/invoiceFunctions.utils';
import { appToast } from 'utils/toast.utils';
import { FetchInvoiceData } from '../InvoiceDetails/InvoiceDetails.types';
import { ConfirmCurrencyChangeDialog } from './ConfirmCurrencyChangeDialog';
import ClausesAutocomplete from './elements/ClausesAutocomplete';
import CorrectedInvoiceAutocomplete from './elements/CorrectedInvoiceAutocomplete';
import CounterpartyAutocomplete from './elements/CounterpartyAutocomplete';
import CurrencyAutocomplete from './elements/CurrencyAutocomplete';
import { InvoiceClassAutocomplete } from './elements/InvoiceClassAutocomplete';
import {
  fieldIsRequired,
  getJobInvoiceRequestBody,
  isCodeFieldVisible,
  isCorrectedInvoiceFieldVisible,
  prepareCreateInvoiceRequestBody,
  prepareEditInvoiceRequestBody,
} from './InvoiceForm.utils';
import {
  currencyCodeValidationRules,
  dueDateValidationRules,
  issueDateValidationRules,
  receiptDateValidationRules,
  saleDateValidationRules,
} from './invoiceFormValidators';
import * as S from './InvoicesForm.css';
import { FormInputs, InvoiceClassType } from './types';

type Props = {
  isEditing?: boolean;
  invoiceData?: Invoice | CorrectiveInvoice;
  cancelEditing?: () => void;
  refetchInvoiceData?: FetchInvoiceData;
};

export const InvoiceForm = ({
  isEditing,
  invoiceData,
  cancelEditing,
  refetchInvoiceData,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation([
    'misc',
    'invoice',
    'counterparty',
    'currency',
    'clause',
    'validation',
  ]);
  const { tab: invoiceTab } = useParams<{ tab: InvoiceTab }>();

  const {
    state: { jobId },
    dispatch,
  } = useMessageContext();

  const [passedJobId, setPassedJobId] = useState<string | null>(null);

  useEffect(
    () => () =>
      dispatch({
        type: MessageContextActionType.CLEAR_JOB_ID,
      }),
    [],
  );

  useEffect(() => {
    if (jobId !== '') {
      setPassedJobId(jobId);
    }
  }, [jobId]);

  const defaultValues = useMemo(
    () => ({
      invoiceClass: invoiceData?.invoiceClass || null,
      code: invoiceData?.code || '',
      correctedInvoice: null,
      counterparty: invoiceData
        ? invoiceData.invoiceClass.own
          ? invoiceData.buyerCounterparty
          : invoiceData.sellerCounterparty
        : null,
      currencyCode: invoiceData?.currencyCode || null,
      issueDate: invoiceData
        ? format(new Date(invoiceData.issueDate), FORM_DATE_FORMAT)
        : '',
      saleDate: invoiceData
        ? format(new Date(invoiceData.saleDate), FORM_DATE_FORMAT)
        : '',
      dueDate: invoiceData
        ? format(new Date(invoiceData.dueDate), FORM_DATE_FORMAT)
        : '',
      receiptDate: invoiceData
        ? format(new Date(invoiceData.receiptDate), FORM_DATE_FORMAT)
        : '',
      exchangeRateDate: invoiceData
        ? format(new Date(invoiceData.exchangeRateDate), FORM_DATE_FORMAT)
        : '',
      clauses: invoiceData?.clauses?.filter((c) => !c.mandatory) || [],
      description: invoiceData?.description || '',
      comment: invoiceData?.comment || '',
    }),
    [invoiceData],
  );

  const {
    control,
    register,
    trigger,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues,
  });

  const currentDateEpoch = new Date().getTime();

  const [confirmCurrencyChangeDialogData, setConfirmCurrencyChangeDialogData] =
    useState<{
      open: boolean;
      newCurrency: CurrencyCode | undefined;
      prevCurrency: CurrencyCode | undefined;
    }>({ open: false, newCurrency: undefined, prevCurrency: undefined });

  const [sellerCounterpartyOptions, setSellerCounterpartyOptions] = useState<
    Counterparty[]
  >([]);
  const [sellerCounterpartyParams, setSellerCounterpartyParams] = useState({
    filterText: '',
    pageNumber: 1,
  });

  const [invoiceClassOptions, setInvoiceClassOptions] = useState<
    InvoiceClass[]
  >([]);
  const [invoiceClassParams, setInvoiceClassParams] =
    useState<InvoiceClassQueryParams>({
      filterText: '',
      pageNumber: 1,
      forNewInvoice: true,
      forNewJobInvoice: false,
    });

  const [correctedInvoiceOptions, setCorrectedInvoiceOptions] = useState<
    Invoice[]
  >([]);
  const [correctedInvoiceParams, setCorrectedInvoiceParams] =
    useState<InvoicesParams>({
      filterText: '',
      pageNumber: 1,
      resultSize: 25,
      own: true,
      excludeCorrective: true,
      excludeDeleted: true,
      published: true,
      invoiceType: 'ADMINISTRATIVE',
      forCorrectiveCreate: true,
      administrative: true,
      hasCorrectiveInvoice: false,
    });

  const [currencyOptions, setCurrencyOptions] = useState<CurrencyCode[]>([]);
  const [currencyParams, setCurrencyParams] = useState({
    filterText: '',
    pageNumber: 1,
  });

  const [clausesOptions, setClausesOptions] = useState<Clause[]>([]);
  const [clausesParams, setClausesParams] = useState<ClausesQueryParams>({
    filterText: '',
    pageNumber: 1,
    initial: false,
    clauseType: 'BODY',
    mandatory: false,
  });

  const { getInvoiceByJobId, data: jobInvoiceData } =
    InvoiceService.useGetInvoiceByJobId();

  const {
    getLastIssueDate,
    loading: lastIssueDateLoading,
    lastIssueDateData,
  } = InvoiceService.useGetLastIssueDate();

  const { getExchangeRateDate, exchangeRateDateData } =
    InvoiceService.useGetExchangeRateDate();

  const {
    getExchangeRateExists,
    exchangeRateExistsData,
    loading: exchangeRateExistsLoading,
  } = InvoiceService.useGetExchangeRateExists();

  const { getExchangeRateValue } = InvoiceService.useGetExchangeRateValue();

  const { getInvoiceById, loading: correctedInvoiceLoading } =
    InvoiceService.useGetInvoiceById();

  const { getCounterpartyById, loading: counterpartyByIdLoading } =
    CounterpartyService.useGetCounterpartyById();

  const { createInvoice, loading: createInvoiceLoading } =
    InvoiceService.useCreateInvoice();

  const { createCorrectiveInvoice, loading: createCorrectiveInvoiceLoading } =
    InvoiceService.useCreateCorrectiveInvoice();

  const { createJobInvoice, loading: createJobInvoiceLoading } =
    InvoiceService.useCreateJobInvoice();

  const { editInvoice, loading: editInvoiceLoading } =
    InvoiceService.useEditInvoice();
  const { editCorrectiveInvoice, loading: editCorrectiveInvoiceLoading } =
    InvoiceService.useEditCorrectiveInvoice();

  const loadingsArray = [
    lastIssueDateLoading,
    correctedInvoiceLoading,
    counterpartyByIdLoading,
    createInvoiceLoading,
    createCorrectiveInvoiceLoading,
    createJobInvoiceLoading,
    exchangeRateExistsLoading,
    editInvoiceLoading,
    editCorrectiveInvoiceLoading,
    !!passedJobId && !getValues('saleDate'), // display loader when jobId is given and data is not loaded yet (saleDate is always given by generated invoice)
  ];

  const currencyCode = watch('currencyCode');
  const saleDate = watch('saleDate');
  const invoiceClass = watch('invoiceClass');
  const correctedInvoice = watch('correctedInvoice');
  const counterpartyData = watch('counterparty');
  const issueDateValue = watch('issueDate');
  const exchangeRateDateValue = watch('exchangeRateDate');

  // check for exchange rate when currency and saledate are selected
  useEffect(() => {
    const saleDateEpoch = saleDate ? new Date(saleDate).getTime() : null;
    if (saleDateEpoch) {
      getExchangeRateDate({
        saleDate: saleDateEpoch,
      })
        .then((res) => {
          currencyCode?.currency &&
            getExchangeRateExists({
              to: currencyCode.currency,
              date: res?.data?.result,
            })
              .then(async (res) => {
                if (res?.data?.result) {
                  const exchangeRateDateEpoch =
                    res?.data?.result[currencyCode.currency];
                  const exchangeRateDate = new Date(exchangeRateDateEpoch);
                  setValue(
                    'exchangeRateDate',
                    format(exchangeRateDate, 'yyyy-MM-dd'),
                    {
                      shouldTouch: true,
                      shouldValidate: true,
                    },
                  );
                  await trigger(['currencyCode']);
                }
              })
              .catch((e) => console.log(e));
        })
        .catch((e) => console.log(e));
    }
  }, [
    getExchangeRateExists,
    currencyCode?.currency,
    saleDate,
    getExchangeRateDate,
  ]);

  useEffect(() => {
    trigger(['currencyCode']);
  }, [exchangeRateDateData, exchangeRateExistsData]);

  const [isDueDateCad, setIsDueDateCad] = useState(false);

  // set default description value when invoice is corrective
  useEffect(() => {
    if (!isEditing) {
      setIsDueDateCad(false);
      setValue('receiptDate', '', {
        shouldTouch: true,
        shouldValidate: true,
      });
      if (!_.isEmpty(invoiceClass) && invoiceClass) {
        setClausesOptions([]);
        const isAdministrative =
          invoiceClass && isInvoiceAdministrative(invoiceClass);
        if (isAdministrative) {
          setClausesParams((prev) => ({
            ...prev,
            documentType: 'ADMINISTRATIVE_INVOICE',
          }));
        } else {
          setClausesParams((prev) => ({
            ...prev,
            documentType: 'INVOICE',
          }));
        }
        if (isInvoiceCorrective(invoiceClass)) {
          setCorrectedInvoiceOptions([]);
          setValue('description', t('complaint', { ns: 'invoice' }));
          getLastIssueDate(InvoiceType.CORRECTIVE_INVOICE, {
            issueDate: currentDateEpoch,
            type: invoiceClass?.type,
          });

          const isNoVat = invoiceClass?.type.toLowerCase().includes('no_vat');

          setCorrectedInvoiceParams((prev: InvoicesParams) => ({
            ...prev,
            own: !!invoiceClass?.own,
            invoiceType: isAdministrative
              ? InvoiceClassType.ADMINISTRATIVE
              : isNoVat
              ? InvoiceClassType.NO_VAT
              : InvoiceClassType.VAT,
            administrative: !!isAdministrative,
          }));
        } else {
          setValue('issueDate', format(new Date(), 'yyyy-MM-dd'), {
            shouldTouch: true,
            shouldValidate: true,
          });
          getLastIssueDate(InvoiceType.INVOICE, {
            issueDate: currentDateEpoch,
            type: invoiceClass.type,
          });
        }
      }
      trigger();
    }
  }, [invoiceClass?.id]);

  // fetch data of corrected invoice
  useEffect(() => {
    if (correctedInvoice?.id) {
      getInvoiceById(correctedInvoice?.id)
        .then((res) => {
          setValue('correctedInvoice', res.data.result as Invoice);
          setValue(
            'counterparty',
            invoiceClass?.code === 'FK'
              ? res?.data?.result?.sellerCounterparty
              : res?.data?.result?.buyerCounterparty,
            {
              shouldValidate: true,
              shouldTouch: true,
            },
          );

          setValue('currencyCode', res?.data?.result?.currencyCode, {
            shouldValidate: true,
            shouldTouch: true,
          });
          const dueDate = new Date(res.data?.result?.dueDate);
          setValue('dueDate', format(dueDate, 'yyyy-MM-dd'), {
            shouldTouch: true,
            shouldValidate: true,
          });
          const exchangeRateDate = new Date(res.data?.result?.exchangeRateDate);
          setValue('exchangeRateDate', format(exchangeRateDate, 'yyyy-MM-dd'), {
            shouldTouch: true,
            shouldValidate: true,
          });
          const saleDateDate = new Date(res.data?.result?.saleDate);
          setValue('saleDate', format(saleDateDate, 'yyyy-MM-dd'), {
            shouldTouch: true,
            shouldValidate: true,
          });
        })
        .catch((e) => console.log(e));
    }
  }, [correctedInvoice?.id]);

  const [showCadWarning, setShowCadWarning] = useState(false);

  // get counterparty details after selection and set dueDate value for own invoices
  useEffect(() => {
    if (counterpartyData?.id && !!issueDateValue) {
      setShowCadWarning(false);
      !isEditing &&
        setValue('receiptDate', getValues('issueDate'), {
          shouldTouch: true,
          shouldValidate: true,
        });
      getCounterpartyById(counterpartyData.id)
        .then((res) => {
          const selectedCurrency = currencyCode?.currency;
          if (selectedCurrency) {
            const dueDays = res.data?.result.dueDays;
            if (
              dueDays &&
              Object.prototype.hasOwnProperty.call(dueDays, selectedCurrency) &&
              issueDateValue
            ) {
              if (dueDays[selectedCurrency] > 0) {
                setIsDueDateCad(false);
                const dueDateValue = addDays(
                  new Date(issueDateValue),
                  dueDays[selectedCurrency],
                );
                setValue('dueDate', format(dueDateValue, 'yyyy-MM-dd'), {
                  shouldTouch: true,
                  shouldValidate: true,
                });
              } else if (dueDays[selectedCurrency] === 0) {
                setIsDueDateCad(true);
                setValue('dueDate', 'CAD', {
                  shouldTouch: true,
                  shouldValidate: true,
                });
              } else {
                setIsDueDateCad(true);
                setValue('dueDate', 'Przedpłata', {
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }
            } else {
              if (invoiceClass?.own) {
                setShowCadWarning(true);
              }
              setIsDueDateCad(true);
              setValue('dueDate', 'CAD', {
                shouldTouch: true,
                shouldValidate: true,
              });
            }
          }
        })
        .catch((e) => console.log(e));
    }
  }, [
    invoiceClass?.own,
    counterpartyData?.id,
    issueDateValue,
    currencyCode?.currency,
    isDueDateCad,
  ]);

  useEffect(() => {
    register('exchangeRateDate', { value: '' });
  }, [register]);

  useEffect(() => {
    if (invoiceClass && isInvoiceInternal(invoiceClass) && !isEditing) {
      const nautiqusCounterparty: Counterparty = {
        id: 1,
        lock: 0,
        enabled: true,
        code: 'NAUTIQUS',
        name: 'NAUTIQUS',
        address: {
          city: '',
          countryCode: '',
          lineOne: '',
          lineTwo: '',
        },
        taxId: {
          number: '',
          type: '',
        },
        accounts: [],
        fromEU: false,
      };
      setValue('counterparty', nautiqusCounterparty, {
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [invoiceClass, isEditing, setValue]);

  useEffect(() => {
    async function loadData() {
      if (passedJobId) {
        await getInvoiceByJobId(Number(passedJobId)).then((res) => {
          setInvoiceClassOptions([]);
          setInvoiceClassParams((prev) => ({
            ...prev,
            forNewInvoice: false,
            forNewJobInvoice: true,
          }));
          const jobInvoiceData = jsonIdDecode(res.data.result, {});
          const data = {
            ...jobInvoiceData,
            clauses: jobInvoiceData.clauses || [],
            issueDate: format(jobInvoiceData.issueDate, FORM_DATE_FORMAT),
            saleDate: format(jobInvoiceData.saleDate, FORM_DATE_FORMAT),
            exchangeRateDate: format(
              jobInvoiceData.exchangeRateDate,
              FORM_DATE_FORMAT,
            ),
            dueDate: jobInvoiceData.dueDate
              ? format(jobInvoiceData.dueDate, FORM_DATE_FORMAT)
              : undefined,
            receiptDate: jobInvoiceData.receiptDate
              ? format(jobInvoiceData.receiptDate, FORM_DATE_FORMAT)
              : undefined,
          };
          reset(data);
          setValue('invoiceClass', jobInvoiceData.invoiceClass, {
            shouldTouch: true,
          });
          setValue('counterparty', jobInvoiceData.buyerCounterparty, {
            shouldTouch: true,
          });
          setValue('currencyCode', jobInvoiceData.currencyCode, {
            shouldTouch: true,
          });
          setValue(
            'saleDate',
            format(jobInvoiceData.saleDate, FORM_DATE_FORMAT),
            {
              shouldTouch: true,
            },
          );
          setValue('description', jobInvoiceData.description || '');
          trigger();
        });
      }
    }
    loadData();
  }, [passedJobId]);

  // recalculate exchange rates when currency changes OR when exchangeRateDate changes
  useEffect(() => {
    if (passedJobId && jobInvoiceData && exchangeRateDateValue) {
      const invoiceData = {
        ...jobInvoiceData,
        ...getValues(),
        exchangeRateDate: dateStringToEpoch(exchangeRateDateValue),
      } as Invoice | CorrectiveInvoice;
      const payload = getExchangeRatesPayload(invoiceData);
      payload &&
        getExchangeRateValue(payload)
          .then((res) => {
            const { totals, taxTotals, invoiceLines } =
              recalculateAllExchangeRates(invoiceData, res.data.result);
            setValue('totals', totals);
            setValue('taxTotals', taxTotals);
            setValue('invoiceLines', invoiceLines);
          })
          .catch((e) => {
            console.log(e);
          });
    }
  }, [currencyCode, exchangeRateDateValue]);

  const handleCancel = () => {
    navigate(
      generatePath(reactPaths.invoicesModule, {
        tab: invoiceTab,
      }),
    );
  };

  const handleConfirmCurrencyChange = async () => {
    setConfirmCurrencyChangeDialogData((prev) => ({
      ...prev,
      open: false,
    }));
    invoiceData &&
      refetchInvoiceData &&
      (await refetchInvoiceData(
        invoiceData.id,
        isInvoiceCorrective(invoiceData.invoiceClass),
      ));
    setValue(
      'currencyCode',
      confirmCurrencyChangeDialogData.newCurrency || null,
    );
  };

  const recalculateInvoiceLines = async (data: any) => {
    const linesCurrencies = invoiceData?.invoiceLines.map(
      (line) => line.currencyAmount.currencyCode.currency,
    );
    if (invoiceData && exchangeRateDateValue && linesCurrencies) {
      const response = await getExchangeRateValue({
        from: linesCurrencies,
        to: data?.currency,
        date: dateStringToEpoch(exchangeRateDateValue),
      });

      const { totals, taxTotals, invoiceLines } = recalculateAllExchangeRates(
        invoiceData,
        response.data.result,
      );
      setValue('totals', totals);
      setValue('taxTotals', taxTotals);
      setValue('invoiceLines', invoiceLines);
    }
  };

  const handleCurrencyEdit = (data: any) => {
    setConfirmCurrencyChangeDialogData({
      prevCurrency: getValues('currencyCode') as CurrencyCode,
      newCurrency: data,
      open: true,
    });
  };

  const onNewInvoiceSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { counterparty, correctedInvoice, code, ...restData } = data;
    const isCorrective = restData.invoiceClass?.type
      .toLowerCase()
      .includes('corrective');
    const isAdministrative =
      restData.invoiceClass && isInvoiceAdministrative(restData.invoiceClass);
    const isInternal =
      !restData.invoiceClass?.own &&
      restData.invoiceClass?.type === 'INTERNAL_CHARGE';
    const isForeign = !restData?.invoiceClass?.own;

    const requestBody = prepareCreateInvoiceRequestBody(data);
    const newInvoiceTab = isInternal
      ? InvoiceTab.INTERNAL
      : isForeign
      ? InvoiceTab.FOREIGN
      : isAdministrative
      ? InvoiceTab.ADMINISTRATIVE
      : InvoiceTab.OWN;
    if (isCorrective) {
      const res = await createCorrectiveInvoice(requestBody);
      if (isSuccessful(res)) {
        appToast.success(t('message.createSuccess', { ns: 'misc' }));

        navigate(
          generatePath(reactPaths.invoiceDetails, {
            tab: newInvoiceTab,
            invoiceType: InvoiceType.CORRECTIVE_INVOICE,
            invoiceId: String(res.data.result?.id),
          }),
        );
      }
      return;
    }
    const res = await createInvoice(requestBody);
    if (isSuccessful(res)) {
      appToast.success(t('message.createSuccess', { ns: 'misc' }));
      navigate(
        generatePath(reactPaths.invoiceDetails, {
          tab: newInvoiceTab,
          invoiceType: InvoiceType.INVOICE,
          invoiceId: String(res.data.result?.id),
        }),
      );
    }
  };

  const onJobInvoiceSubmit: SubmitHandler<FormInputs> = async () => {
    if (passedJobId) {
      const payload = getJobInvoiceRequestBody(getValues());
      const res = await createJobInvoice(Number(passedJobId), payload);
      if (isSuccessful(res)) {
        appToast.success(t('message.createSuccess', { ns: 'misc' }));
        navigate(
          generatePath(reactPaths.invoiceDetails, {
            tab: invoiceTab,
            invoiceType: InvoiceType.INVOICE,
            invoiceId: String(res.data.result.id),
          }),
        );
      }
    }
  };

  const onEditInvoiceSubmit: SubmitHandler<FormInputs> = async (data) => {
    const defaultPayload = prepareEditInvoiceRequestBody(data, invoiceData);
    if (
      data?.saleDate &&
      dateStringToEpoch(data.saleDate) === invoiceData?.saleDate
    ) {
      const res = isInvoiceCorrective(defaultPayload.invoiceClass)
        ? await editCorrectiveInvoice(invoiceData.id, defaultPayload)
        : await editInvoice(invoiceData.id, defaultPayload);
      isSuccessful(res) &&
        appToast.success(t('message.updateSuccess', { ns: 'misc' }));
      await refetchInvoiceData!(
        invoiceData!.id,
        isInvoiceCorrective(defaultPayload.invoiceClass),
      );
      cancelEditing && cancelEditing();
      return;
    }
    if (isInvoiceCorrective(defaultPayload.invoiceClass) && invoiceData) {
      const exchangeRatesPayload = await getCorrectedExchangeRatesPayload(
        defaultPayload,
      );
      const recalculatedPayload =
        exchangeRatesPayload &&
        (await getExchangeRateValue(exchangeRatesPayload).then((res) => {
          const recalculatedFields = recalculateAllCorrectedExchangeRates(
            defaultPayload,
            res.data.result,
          );
          return recalculateTotals(
            recalculateCorrectedTotals({
              ...defaultPayload,
              ...recalculatedFields,
            }),
          );
        }));
      const res = await editCorrectiveInvoice(
        invoiceData?.id,
        recalculatedPayload || defaultPayload,
      );
      isSuccessful(res) &&
        appToast.success(t('message.updateSuccess', { ns: 'misc' }));
      await refetchInvoiceData!(invoiceData.id, true);
      cancelEditing && cancelEditing();
      return;
    }
    const exchangeRatesPayload = getExchangeRatesPayload(defaultPayload);
    if (!!exchangeRatesPayload && invoiceData) {
      const recalculatedPayload = await getExchangeRateValue(
        exchangeRatesPayload,
      ).then((res) => {
        const recalculatedFields = recalculateAllExchangeRates(
          defaultPayload,
          res.data.result,
        );
        return { ...defaultPayload, ...recalculatedFields };
      });
      const res = await editInvoice(invoiceData.id, recalculatedPayload);
      isSuccessful(res) &&
        appToast.success(t('message.updateSuccess', { ns: 'misc' }));
    } else {
      const res = await editInvoice(invoiceData!.id, defaultPayload);
      isSuccessful(res) &&
        appToast.success(t('message.updateSuccess', { ns: 'misc' }));
    }
    await refetchInvoiceData!(invoiceData!.id, false);
    cancelEditing && cancelEditing();
  };

  return (
    <Box display='flex' flexDirection='column'>
      <LoaderFullScreen loading={loadingsArray.some((l) => l)} />
      {isEditing && invoiceData && (
        <ConfirmCurrencyChangeDialog
          open={confirmCurrencyChangeDialogData.open}
          invoiceData={invoiceData}
          onClose={() =>
            setConfirmCurrencyChangeDialogData((prev) => ({
              ...prev,
              open: false,
            }))
          }
          onSubmit={handleConfirmCurrencyChange}
        />
      )}
      <S.Form
        onSubmit={handleSubmit(
          isEditing
            ? onEditInvoiceSubmit
            : !passedJobId
            ? onNewInvoiceSubmit
            : onJobInvoiceSubmit,
        )}
      >
        {!isEditing && (
          <S.Title>{t('createInvoice', { ns: 'invoice' })}</S.Title>
        )}

        <S.FieldGroup $hidden={isEditing}>
          <S.FieldLabel htmlFor='invoiceClass'>
            {t('invoiceType', { ns: 'invoice' })}
          </S.FieldLabel>
          <Controller
            name='invoiceClass'
            control={control}
            rules={fieldIsRequired}
            render={(props) => (
              <InvoiceClassAutocomplete
                formProps={props}
                options={invoiceClassOptions}
                setOptions={setInvoiceClassOptions}
                params={invoiceClassParams}
                setParams={setInvoiceClassParams}
                error={!!errors.invoiceClass}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.invoiceClass && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        {(isEditing ||
          !invoiceClass?.id ||
          isCodeFieldVisible(invoiceClass?.code)) && (
          <S.FieldGroup>
            <S.FieldLabel htmlFor='code'>
              {t('code', { ns: 'invoice' })}
            </S.FieldLabel>
            <StyledTextField
              id='code'
              padding='dense'
              size='small'
              disabled={isEditing}
              variant='outlined'
              width={280}
              error={!!errors.code}
              {...register('code', {
                required: true,
                maxLength: {
                  value: 64,
                  message: t('maxLength', {
                    ns: 'validation',
                    replace: { '0': '64' },
                  }),
                },
              })}
            />
            <S.ErrorWrapper>
              {errors.code && (
                <S.Error>
                  {errors?.code?.message || t('required', { ns: 'validation' })}
                </S.Error>
              )}
            </S.ErrorWrapper>
          </S.FieldGroup>
        )}

        {!!invoiceClass && isCorrectedInvoiceFieldVisible(invoiceClass?.code) && (
          <S.FieldGroup>
            <S.FieldLabel htmlFor='correctedInvoice'>
              {t('forInvoice', { ns: 'invoice' })}
            </S.FieldLabel>
            <Controller
              name='correctedInvoice'
              control={control}
              rules={{ required: !isEditing }}
              render={(props) => (
                <CorrectedInvoiceAutocomplete
                  formProps={props}
                  invoiceData={invoiceData}
                  options={correctedInvoiceOptions}
                  params={correctedInvoiceParams}
                  setOptions={setCorrectedInvoiceOptions}
                  setParams={setCorrectedInvoiceParams}
                  isEditing={isEditing}
                  error={!!errors.correctedInvoice}
                />
              )}
            />
            <S.ErrorWrapper>
              {errors.correctedInvoice && (
                <S.Error>{t('required', { ns: 'validation' })}</S.Error>
              )}
            </S.ErrorWrapper>
          </S.FieldGroup>
        )}
        {isEditing && !invoiceClass?.own && (
          <S.FieldGroup>
            <S.FieldLabel htmlFor='logNumber'>
              {t('logNumber', { ns: 'invoice' })}
            </S.FieldLabel>
            <StyledTextField
              id='logNumber'
              padding='dense'
              size='small'
              defaultValue={invoiceData?.logNumber}
              disabled
              variant='outlined'
              width={280}
            />
            <S.ErrorWrapper />
          </S.FieldGroup>
        )}
        <S.FieldGroup>
          <S.FieldLabel>
            {t('counterparty', { ns: 'counterparty' })}
          </S.FieldLabel>
          <Controller
            name='counterparty'
            control={control}
            rules={fieldIsRequired}
            render={(props) => (
              <CounterpartyAutocomplete
                formProps={props}
                invoiceClassValue={invoiceClass}
                options={sellerCounterpartyOptions}
                params={sellerCounterpartyParams}
                setOptions={setSellerCounterpartyOptions}
                setParams={setSellerCounterpartyParams}
                isEditing={isEditing}
                error={!!errors.counterparty}
                isDisabled={!!(invoiceClass && isInvoiceInternal(invoiceClass))}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.counterparty && (
              <S.Error>{t('required', { ns: 'validation' })}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel>{t('currency', { ns: 'currency' })}</S.FieldLabel>
          <Controller
            name='currencyCode'
            control={control}
            rules={currencyCodeValidationRules(
              exchangeRateDateData,
              exchangeRateExistsData,
            )}
            render={(props) => (
              <CurrencyAutocomplete
                formProps={props}
                options={currencyOptions}
                setOptions={setCurrencyOptions}
                params={currencyParams}
                setParams={setCurrencyParams}
                handleCurrencyEdit={handleCurrencyEdit}
                handleOwnCurrencyEdit={recalculateInvoiceLines}
                invoiceClassValue={invoiceClass}
                invoiceData={invoiceData}
                isEditing={isEditing}
                error={!!errors.currencyCode}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.currencyCode && (
              <S.Error>
                {(errors.currencyCode as any)?.type !== 'exchangeRate' &&
                  t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        {(errors.currencyCode as any)?.message && (
          <S.ErrorBlockWrapper>
            <S.Error>{(errors.currencyCode as any)?.message}</S.Error>
          </S.ErrorBlockWrapper>
        )}

        <S.FieldGroup>
          <S.FieldLabel>{t('issueDate', { ns: 'invoice' })}</S.FieldLabel>
          <StyledTextField
            id='issueDate'
            size='small'
            disabled={isEditing && invoiceClass?.own}
            padding='dense'
            width={280}
            type='date'
            error={!!errors.issueDate}
            InputLabelProps={{
              shrink: true,
            }}
            {...register(
              'issueDate',
              issueDateValidationRules(
                trigger,
                lastIssueDateData,
                invoiceClass,
                isEditing,
              ),
            )}
          />
          <S.ErrorWrapper>
            {errors.issueDate && (
              <S.Error>
                {errors?.issueDate?.message ||
                  t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel>{t('saleDate', { ns: 'invoice' })}</S.FieldLabel>
          <StyledTextField
            id='saleDate'
            padding='dense'
            size='small'
            type='date'
            width={280}
            error={!!errors.saleDate}
            InputLabelProps={{
              shrink: true,
            }}
            {...register('saleDate', saleDateValidationRules(issueDateValue))}
          />
          <S.ErrorWrapper>
            {errors.saleDate && (
              <S.Error>
                {errors?.saleDate?.message ||
                  t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel>{t('dueDate', { ns: 'invoice' })}</S.FieldLabel>
          <StyledTextField
            id='dueDate'
            padding='dense'
            size='small'
            type={isDueDateCad ? 'text' : 'date'}
            disabled={isDueDateCad || invoiceClass?.own}
            error={!!errors.dueDate}
            width={280}
            InputLabelProps={{
              shrink: true,
            }}
            {...register(
              'dueDate',
              dueDateValidationRules(issueDateValue, isDueDateCad),
            )}
          />
          <S.ErrorWrapper>
            {errors.dueDate && (
              <S.Error>
                {errors?.dueDate?.message ||
                  t('required', { ns: 'validation' })}
              </S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>
        {showCadWarning && (
          <S.ErrorBlockWrapper>
            <S.Error>{t('message.lackOfDueDays', { ns: 'misc' })}</S.Error>
          </S.ErrorBlockWrapper>
        )}

        {!invoiceClass?.own && (
          <S.FieldGroup>
            <S.FieldLabel>{t('receiptDate', { ns: 'invoice' })}</S.FieldLabel>
            <StyledTextField
              id='receiptDate'
              padding='dense'
              size='small'
              type='date'
              width={280}
              error={!!errors.receiptDate}
              InputLabelProps={{
                shrink: true,
              }}
              {...register(
                'receiptDate',
                receiptDateValidationRules(isEditing),
              )}
            />
            <S.ErrorWrapper>
              {errors.receiptDate && (
                <S.Error>
                  {errors?.receiptDate?.message ||
                    t('required', { ns: 'validation' })}
                </S.Error>
              )}
            </S.ErrorWrapper>
          </S.FieldGroup>
        )}

        {invoiceClass?.own && (
          <S.FieldGroup>
            <S.FieldLabel>
              {t('exchangeRateDate', { ns: 'invoice' })}
            </S.FieldLabel>
            <StyledTextField
              disabled
              id='exchangeRateDate'
              padding='dense'
              size='small'
              width={280}
              type='date'
              InputLabelProps={{
                shrink: true,
              }}
              {...register('exchangeRateDate', {
                required: true,
              })}
            />
            <S.ErrorWrapper />
          </S.FieldGroup>
        )}

        {invoiceClass?.own && (
          <S.FieldGroup>
            <S.FieldLabel>{t('body', { ns: 'clause' })}</S.FieldLabel>
            <Controller
              name='clauses'
              control={control}
              render={(props) => (
                <ClausesAutocomplete
                  formProps={props}
                  options={clausesOptions}
                  setOptions={setClausesOptions}
                  params={clausesParams}
                  setParams={setClausesParams}
                />
              )}
            />
            <S.ErrorWrapper />
          </S.FieldGroup>
        )}

        <S.FieldGroup>
          <S.FieldLabel>{t('description', { ns: 'misc' })}</S.FieldLabel>
          <Controller
            name='description'
            control={control}
            rules={{
              maxLength: {
                value: 256,
                message: t('maxLength', {
                  ns: 'validation',
                  replace: { '0': '256' },
                }),
              },
            }}
            render={({ field }) => (
              <StyledTextField
                {...field}
                padding='dense'
                size='small'
                variant='outlined'
                margin='none'
                error={!!errors.description}
                width={280}
              />
            )}
          />
          <S.ErrorWrapper>
            {errors.description && (
              <S.Error>{errors?.description?.message}</S.Error>
            )}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.FieldGroup>
          <S.FieldLabel>{t('comment', { ns: 'misc' })}</S.FieldLabel>
          <StyledTextField
            id='comment'
            padding='dense'
            size='small'
            variant='outlined'
            width={280}
            error={!!errors.comment}
            {...register('comment', {
              maxLength: {
                value: 256,
                message: t('maxLength', {
                  ns: 'validation',
                  replace: { '0': '256' },
                }),
              },
            })}
          />
          <S.ErrorWrapper>
            {errors.comment && <S.Error>{errors?.comment?.message}</S.Error>}
          </S.ErrorWrapper>
        </S.FieldGroup>

        <S.ActionsContainer>
          <StyledButton $primary type='submit' disabled={!_.isEmpty(errors)}>
            {t('button.save', { ns: 'misc' })}
          </StyledButton>
          <StyledButton onClick={!isEditing ? handleCancel : cancelEditing}>
            {t('button.cancel', { ns: 'misc' })}
          </StyledButton>
        </S.ActionsContainer>
      </S.Form>
    </Box>
  );
};
